import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Button,
  Spinner,
  Box,
  Divider,
  Modal,
  TextContainer,
  List,
  Badge,
  TextField,
  InlineStack
} from '@shopify/polaris';
import { useTranslation } from 'react-i18next';
import { useShopifyNavigation } from '../hooks/useShopifyNavigation';
import api from '../utils/axiosConfig';
import UpsellSettings from '../components/UpsellSettings';

function Personalization() {
  const { t } = useTranslation();
  const navigateWithContext = useShopifyNavigation();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [pages, setPages] = useState([]);
  const [scopes, setScopes] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmModalActive, setConfirmModalActive] = useState(false);
  const [deleteModalActive, setDeleteModalActive] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pageToDelete, setPageToDelete] = useState(null);
  const [shopDomain, setShopDomain] = useState('sua-loja.myshopify.com');
  const [pageSettings, setPageSettings] = useState({
    title: 'Track Your Order',
    handle: 'trackingmania'
  });

  useEffect(() => {
    // Add a small delay to ensure App Bridge is initialized
    const timer = setTimeout(() => {
      loadPersonalizationData();
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  const loadPersonalizationData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Check if we're in a Shopify context and get shop domain
      const shopifyDomain = window.SHOPIFY_SHOP || 
                           sessionStorage.getItem('shop') ||
                           new URLSearchParams(window.location.search).get('shop');
      
      if (!shopifyDomain) {
        console.warn('⚠️ No Shopify context available for personalization');
        setError('Please access this page through your Shopify admin');
        return;
      }
      
      // Set the shop domain for display
      setShopDomain(shopifyDomain);
      
      // Load existing pages and scope information in parallel
      const [pagesResponse, scopesResponse] = await Promise.all([
        api.get('/shopify-pages/pages').catch(() => ({ data: { pages: [] } })),
        api.get('/shopify-pages/check-scopes').catch(() => ({ data: { hasRequiredScopes: false } }))
      ]);
      
      setPages(pagesResponse.data.pages || []);
      setScopes(scopesResponse.data);
      
    } catch (err) {
      console.error('Error loading personalization data:', err);
      setError('Erro ao carregar dados de personalização');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePage = async (page) => {
    try {
      setDeleting(true);
      setError('');
      setSuccess('');
      
      console.log('Deleting page:', page);
      console.log('Page ID before encoding:', page.shopify_page_id);
      
      // Encode the page ID to handle special characters in GraphQL IDs
      const encodedPageId = encodeURIComponent(page.shopify_page_id);
      console.log('Encoded page ID:', encodedPageId);
      
      const response = await api.delete(`/shopify-pages/delete-page/${encodedPageId}`);
      
      setSuccess(response.data.message || 'Página removida com sucesso!');
      setDeleteModalActive(false);
      setPageToDelete(null);
      
      // Reload pages to update the list
      await loadPersonalizationData();
      
      // Auto-hide success message after 5 seconds
      setTimeout(() => setSuccess(''), 5000);
      
    } catch (err) {
      console.error('Error deleting page:', err);
      const errorMessage = err.response?.data?.error || 'Erro ao remover página de rastreamento';
      setError(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const confirmDeletePage = (page) => {
    setPageToDelete(page);
    setDeleteModalActive(true);
  };

  const handleCreatePage = async () => {
    try {
      setCreating(true);
      setError('');
      setSuccess('');
      
      const response = await api.post('/shopify-pages/create-page', pageSettings);
      
      const { page, menu, message } = response.data;
      
      let successMessage = message;
      if (menu.published) {
        successMessage += ` A página foi adicionada ao menu ${menu.publishedTo} automaticamente.`;
      } else {
        successMessage += ' Recomendamos adicionar a página ao menu principal da sua loja.';
      }
      
      setSuccess(successMessage);
      setConfirmModalActive(false);
      
      // Reload pages to show the new one
      await loadPersonalizationData();
      
      // Auto-hide success message after 7 seconds (longer for menu info)
      setTimeout(() => setSuccess(''), 7000);
      
    } catch (err) {
      console.error('Error creating page:', err);
      const errorMessage = err.response?.data?.error || 'Erro ao criar página de rastreamento';
      setError(errorMessage);
      
      // If it's a scope issue, refresh scope info
      if (err.response?.status === 403 || err.response?.status === 401) {
        await loadPersonalizationData();
      }
    } finally {
      setCreating(false);
    }
  };

  const getStatusBadge = (hasScopes) => {
    if (hasScopes) {
      return <Badge status="success">{t('personalization.permissionsOk')}</Badge>;
    }
    return <Badge status="warning">{t('personalization.permissionsNeeded')}</Badge>;
  };

  if (loading) {
    return (
      <Page title={t('personalization.title')}>
        <Layout>
          <Layout.Section>
            <Card sectioned>
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Spinner size="large" />
                <Text variant="bodyMd" as="p" color="subdued">
                  {t('personalization.loadingPersonalizationSettings')}
                </Text>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const hasExistingPage = pages.length > 0;
  const canCreatePage = scopes?.hasRequiredScopes && !hasExistingPage;

  return (
    <Page
      title={t('personalization.title')}
      subtitle={t('personalization.subtitle')}
    >
      <Layout>
        {success && (
          <Layout.Section>
            <div style={{
              backgroundColor: '#f0fdf4',
              border: '1px solid #22c55e',
              borderRadius: '6px',
              padding: '16px'
            }}>
              <h4 style={{ color: '#166534', margin: '0 0 8px 0' }}>{t('personalization.success')}</h4>
              <p style={{ color: '#166534', margin: '0' }}>{success}</p>
            </div>
          </Layout.Section>
        )}

        {error && (
          <Layout.Section>
            <div style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #ef4444',
              borderRadius: '6px',
              padding: '16px'
            }}>
              <h4 style={{ color: '#dc2626', margin: '0 0 8px 0' }}>{t('personalization.error')}</h4>
              <p style={{ color: '#dc2626', margin: '0' }}>{error}</p>
            </div>
          </Layout.Section>
        )}

        {/* Scope Requirements Warning */}
        {scopes && !scopes.hasRequiredScopes && (
          <Layout.Section>
            <div style={{
              backgroundColor: '#fffbeb',
              border: '1px solid #f59e0b',
              borderRadius: '6px',
              padding: '16px'
            }}>
              <h4 style={{ color: '#92400e', margin: '0 0 12px 0' }}>{t('personalization.newPermissionsRequired')}</h4>
              <TextContainer>
                <p style={{ color: '#92400e', margin: '0 0 12px 0' }}>
                  <strong>{t('personalization.trackingManiaUpdated')}</strong> {t('personalization.additionalPermissionsNeeded')}
                </p>
                
                <p style={{ color: '#92400e', margin: '0 0 12px 0' }}>{t('personalization.followingPermissionsRequired')}</p>
                <List type="bullet">
                  {scopes.missingScopes?.map(scope => (
                    <List.Item key={scope}>
                      <span style={{ color: '#92400e' }}>
                        <strong>{scope}</strong> - {
                          scope === 'write_online_store_pages' ? t('personalization.createPages') :
                          scope === 'write_content' ? t('personalization.manageContent') :
                          scope === 'read_online_store_navigation' ? t('personalization.readNavigation') :
                          scope === 'write_online_store_navigation' ? t('personalization.writeNavigation') :
                          t('personalization.permissionRequired')
                        }
                      </span>
                    </List.Item>
                  ))}
                </List>
                
                <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#fef7e0', borderRadius: '6px', border: '1px solid #fed07a' }}>
                  <Text variant="bodyMd" fontWeight="semibold" as="p" style={{ marginBottom: '0.5rem', color: '#92400e' }}>
                    🔄 {t('personalization.howToResolve')}
                  </Text>
                  <Text variant="bodyMd" as="p" style={{ color: '#92400e' }}>
                    <strong>1.</strong> {t('personalization.uninstallTrackingMania')}
                  </Text>
                  <Text variant="bodyMd" as="p" style={{ color: '#92400e' }}>
                    <strong>2.</strong> {t('personalization.reinstallApp')}
                  </Text>
                  <Text variant="bodyMd" as="p" style={{ color: '#92400e' }}>
                    <strong>3.</strong> {t('personalization.acceptNewPermissions')}
                  </Text>
                </div>
                
                <p style={{ marginTop: '1rem' }}>
                  <Text variant="bodySm" as="span" style={{ color: '#92400e' }}>
                    {t('personalization.dataWillBeMaintained')}
                  </Text>
                </p>
              </TextContainer>
            </div>
          </Layout.Section>
        )}

        {/* Tracking Page Section */}
        <Layout.Section>
          <Card>
            <Box padding="4">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <Text variant="headingMd" as="h3">
                  {t('personalization.trackingPage')}
                </Text>
                {scopes && getStatusBadge(scopes.hasRequiredScopes)}
              </div>
            </Box>
            
            <Divider />
            
            <Box padding="4">
              {hasExistingPage ? (
                // Show existing page info
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ padding: '16px', backgroundColor: '#f6f6f7', borderRadius: '8px' }}>
                    <Text variant="bodyMd" fontWeight="semibold" as="p">
                      ✅ {t('personalization.trackingPageActive')}
                    </Text>
                    <Text variant="bodySm" color="subdued" as="p" style={{ marginTop: '4px' }}>
                      {t('personalization.customersCanTrack')}
                    </Text>
                  </div>
                  
                  {pages.map(page => (
                    <div key={page.id} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      padding: '16px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      border: '1px solid #e1e3e5'
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                        <Text variant="bodyMd" as="p">
                          <strong>{t('personalization.title')}:</strong> {page.title}
                        </Text>
                        <Text variant="bodyMd" as="p">
                          <strong>{t('personalization.url')}:</strong>{' '}
                          <a href={page.url} target="_blank" rel="noopener noreferrer" style={{ color: '#0066cc' }}>
                            {page.url}
                          </a>
                        </Text>
                        <Text variant="bodySm" color="subdued" as="p">
                          {t('personalization.createdOn')}: {new Date(page.created_at).toLocaleDateString()}
                        </Text>
                      </div>
                      <div style={{ marginLeft: '16px' }}>
                        <Button
                          destructive
                          size="slim"
                          onClick={() => confirmDeletePage(page)}
                          disabled={deleting}
                        >
                          🗑️ {t('personalization.remove')}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Show creation interface
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <TextContainer>
                    <p>
                      {t('personalization.createDedicatedPage')}
                      {t('personalization.pageIntegrationDescription')}
                    </p>
                  </TextContainer>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <TextField
                      label={t('personalization.pageTitle')}
                      value={pageSettings.title}
                      onChange={(value) => setPageSettings(prev => ({ ...prev, title: value }))}
                      placeholder="Track Your Order"
                      helpText={t('personalization.pageTitleHelp')}
                    />
                    
                    <TextField
                      label={t('personalization.pageUrl')}
                      value={pageSettings.handle}
                      onChange={(value) => setPageSettings(prev => ({ ...prev, handle: value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
                      placeholder="trackingmania"
                      prefix={`${shopDomain}/pages/`}
                      helpText={t('personalization.pageUrlHelp')}
                    />
                  </div>

                  <div style={{ marginTop: '16px' }}>
                    <Text variant="headingXs" as="h4" style={{ marginBottom: '8px' }}>
                      {t('personalization.whatWillBeIncluded')}
                    </Text>
                    <List type="bullet">
                      <List.Item>
                        {t('personalization.integratedTrackingForm')}
                      </List.Item>
                      <List.Item>
                        {t('personalization.responsiveDesign')}
                      </List.Item>
                      <List.Item>
                        {t('personalization.multipleTrackingCodes')}
                      </List.Item>
                      <List.Item>
                        {t('personalization.carrierIntegration')}
                      </List.Item>
                      <List.Item>
                        {t('personalization.automaticStatusUpdates')}
                      </List.Item>
                    </List>
                  </div>

                  <div style={{ marginTop: '24px' }}>
                    <Button
                      primary
                      onClick={() => setConfirmModalActive(true)}
                      disabled={!canCreatePage}
                    >
                      {t('personalization.createPage')}
                    </Button>
                    
                    {!scopes?.hasRequiredScopes && (
                      <Text variant="bodySm" color="subdued" as="p" style={{ marginTop: '8px' }}>
                        {t('personalization.permissionsNeededToCreate')}
                      </Text>
                    )}
                  </div>
                </div>
              )}
            </Box>
          </Card>
        </Layout.Section>

        {/* Upsell Offers Section */}
        <Layout.Section>
          <UpsellSettings />
        </Layout.Section>

        {/* Benefits Section */}
        <Layout.Section>
          <Card>
            <Box padding="4">
              <Text variant="headingMd" as="h3" style={{ marginBottom: '16px' }}>
                {t('personalization.trackingPageBenefits')}
              </Text>
            </Box>
            
            <Divider />
            
            <Box padding="4">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                <div>
                  <Text variant="bodyMd" fontWeight="semibold" as="p" style={{ marginBottom: '8px' }}>
                    {t('personalization.integratedExperience')}
                  </Text>
                  <Text variant="bodySm" color="subdued" as="p">
                    {t('personalization.integratedExperienceDesc')}
                  </Text>
                </div>
                
                <div>
                  <Text variant="bodyMd" fontWeight="semibold" as="p" style={{ marginBottom: '8px' }}>
                    {t('personalization.customDesign')}
                  </Text>
                  <Text variant="bodySm" color="subdued" as="p">
                    {t('personalization.customDesignDesc')}
                  </Text>
                </div>
                
                <div>
                  <Text variant="bodyMd" fontWeight="semibold" as="p" style={{ marginBottom: '8px' }}>
                    {t('personalization.responsive')}
                  </Text>
                  <Text variant="bodySm" color="subdued" as="p">
                    {t('personalization.responsiveDesc')}
                  </Text>
                </div>
                
                <div>
                  <Text variant="bodyMd" fontWeight="semibold" as="p" style={{ marginBottom: '8px' }}>
                    {t('personalization.easyToUse')}
                  </Text>
                  <Text variant="bodySm" color="subdued" as="p">
                    {t('personalization.easyToUseDesc')}
                  </Text>
                </div>
              </div>
            </Box>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Create Page Confirmation Modal */}
      <Modal
        open={confirmModalActive}
        onClose={() => setConfirmModalActive(false)}
        title={t('personalization.confirmPageCreation')}
        primaryAction={{
          content: t('personalization.createPageAction'),
          onAction: handleCreatePage,
          loading: creating
        }}
        secondaryActions={[
          {
            content: t('personalization.cancel'),
            onAction: () => setConfirmModalActive(false)
          }
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <p>
              {t('personalization.aboutToCreate')}
            </p>
            
            <div style={{ margin: '16px 0', padding: '16px', backgroundColor: '#f6f6f7', borderRadius: '8px' }}>
              <Text variant="bodyMd" as="p">
                <strong>{t('personalization.title')}:</strong> {pageSettings.title}
              </Text>
              <Text variant="bodyMd" as="p">
                <strong>{t('personalization.url')}:</strong> {shopDomain}/pages/{pageSettings.handle}
              </Text>
            </div>
            
            <p>
              {t('personalization.pageWillBeCreated')}
            </p>
            
            <p>
              <strong>{t('personalization.wantToContinue')}</strong>
            </p>
          </TextContainer>
        </Modal.Section>
      </Modal>

      {/* Delete Page Confirmation Modal */}
      <Modal
        open={deleteModalActive}
        onClose={() => setDeleteModalActive(false)}
        title={t('personalization.confirmRemoval')}
        primaryAction={{
          content: t('personalization.yesRemove'),
          onAction: () => handleDeletePage(pageToDelete),
          loading: deleting,
          destructive: true
        }}
        secondaryActions={[
          {
            content: t('personalization.cancel'),
            onAction: () => {
              setDeleteModalActive(false);
              setPageToDelete(null);
            }
          }
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <p>
              <strong>{t('personalization.confirmRemoveTrackingPage')}</strong>
            </p>
            
            {pageToDelete && (
              <div style={{ margin: '16px 0', padding: '16px', backgroundColor: '#fef2f2', borderRadius: '8px', border: '1px solid #fca5a5' }}>
                <Text variant="bodyMd" as="p">
                  <strong>{t('personalization.page')}:</strong> {pageToDelete.title}
                </Text>
                <Text variant="bodyMd" as="p">
                  <strong>{t('personalization.url')}:</strong> {pageToDelete.url}
                </Text>
              </div>
            )}
            
            <p>
              {t('personalization.actionCannotBeUndone')}
            </p>
            
            <p>
              {t('personalization.customersWillNotAccess')}
            </p>
            
            <div style={{ 
              marginTop: '16px', 
              padding: '12px', 
              backgroundColor: '#fffbeb', 
              borderRadius: '6px', 
              border: '1px solid #fed7aa' 
            }}>
              <Text variant="bodySm" as="p" style={{ color: '#92400e' }}>
                💡 <strong>{t('personalization.tip')}:</strong> {t('personalization.canCreateNewPage')}
              </Text>
            </div>
          </TextContainer>
        </Modal.Section>
      </Modal>
    </Page>
  );
}

export default Personalization;